import React, { useState, useRef, useEffect } from 'react';
import { assetsApi } from '../lib/adminApi';

/**
 * Asset Upload Form Component
 * Handles large file uploads using pre-signed URLs
 */
const AssetUploadForm = ({ projectId, projects = [], onUploadComplete, onCancel }) => {
  const [file, setFile] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(projectId || '');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  
  // Track current upload for cleanup
  const currentUploadRef = useRef(null);
  
  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      // Abort any ongoing upload when component unmounts
      if (currentUploadRef.current) {
        const { r2_key, upload_id } = currentUploadRef.current;
        assetsApi.abortUpload({ r2_key, upload_id }).catch(err => {
          console.error('Failed to abort upload on unmount:', err);
        });
      }
    };
  }, []);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file size (max 5GB)
      if (selectedFile.size > 5 * 1024 * 1024 * 1024) {
        setError('File size must be less than 5GB');
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const uploadFile = async () => {
    if (!file || !selectedProjectId) {
      setError('Please select a file and project');
      return;
    }

    setUploading(true);
    setProgress(0);
    setError(null);
    
    let uploadData = null;

    try {
      // Step 1: Initialize multipart upload
      uploadData = await assetsApi.initializeUpload({
        project_id: selectedProjectId,
        filename: file.name,
        file_size: file.size,
        content_type: file.type
      });
      
      // Track upload for cleanup
      currentUploadRef.current = {
        r2_key: uploadData.r2_key,
        upload_id: uploadData.upload_id
      };

      // Step 2: Upload file parts with progress tracking
      const completedParts = await uploadMultipart(uploadData);
      
      // Step 3: Complete multipart upload
      console.log('Completing upload with parts:', completedParts);
      console.log('Parts validation:', completedParts.map(p => ({ partNumber: p.partNumber, hasEtag: !!p.etag })));
      
      const assetData = await assetsApi.completeUpload({
        asset_id: uploadData.asset_id,
        r2_key: uploadData.r2_key,
        upload_id: uploadData.upload_id,
        parts: completedParts,
        project_id: uploadData.project_id,
        filename: uploadData.filename,
        description: description,
        content_type: uploadData.content_type
      });
      
      // Success! Clear tracking since upload completed
      currentUploadRef.current = null;
      setProgress(100);
      onUploadComplete(assetData);
      
      // Reset form
      setFile(null);
      setDescription('');
      setUploading(false);
      setProgress(0);
      
    } catch (err) {
      console.error('Upload failed:', err);
      
      // Cleanup: Abort multipart upload if it was initialized
      if (uploadData && uploadData.upload_id) {
        try {
          console.log('Aborting multipart upload due to error...');
          await assetsApi.abortUpload({
            r2_key: uploadData.r2_key,
            upload_id: uploadData.upload_id
          });
          console.log('Multipart upload aborted successfully');
        } catch (abortError) {
          console.error('Failed to abort multipart upload:', abortError);
          // Don't throw - we still want to show the original error
        }
      }
      
      setError(err.message);
      setUploading(false);
      setProgress(0);
    }
  };

  const cancelUpload = async () => {
    if (currentUploadRef.current && uploading) {
      try {
        console.log('Cancelling upload...');
        await assetsApi.abortUpload({
          r2_key: currentUploadRef.current.r2_key,
          upload_id: currentUploadRef.current.upload_id
        });
        console.log('Upload cancelled successfully');
      } catch (error) {
        console.error('Failed to cancel upload:', error);
      }
      
      // Reset state
      currentUploadRef.current = null;
      setUploading(false);
      setProgress(0);
      setError(null);
    }
  };

  const uploadMultipart = async (uploadData) => {
    const { part_size, total_parts, urls } = uploadData;
    const completedParts = [];
    let uploadedBytes = 0;
    let currentPartUrls = [...urls];
    
    console.log('Starting multipart upload:', { part_size, total_parts, initialUrls: urls.length });
    
    for (let partNumber = 1; partNumber <= total_parts; partNumber++) {
      // Get part URL (request more if needed)
      if (partNumber > currentPartUrls.length) {
        const additionalUrls = await assetsApi.getPartUrls({
          r2_key: uploadData.r2_key,
          upload_id: uploadData.upload_id,
          start_part: currentPartUrls.length + 1,
          count: Math.min(10, total_parts - currentPartUrls.length)
        });
        currentPartUrls.push(...additionalUrls.urls);
      }
      
      const partUrl = currentPartUrls.find(u => u.partNumber === partNumber);
      if (!partUrl) {
        throw new Error(`Part URL not found for part ${partNumber}`);
      }
      
      // Calculate part boundaries
      const start = (partNumber - 1) * part_size;
      const end = Math.min(start + part_size, file.size);
      const partData = file.slice(start, end);
      
      // Upload this part
      const etag = await uploadPart(partUrl.url, partData, partNumber, uploadedBytes);
      
      console.log(`Part ${partNumber} completed with ETag:`, etag);
      
      const cleanedPart = {
        partNumber: partNumber,  // Use lowercase to match R2 API
        etag: etag.replace(/"/g, '')  // Remove quotes from ETag
      };
      
      console.log('Adding completed part:', cleanedPart);
      completedParts.push(cleanedPart);
      
      uploadedBytes += partData.size;
      
      // Update progress
      const progressPercent = Math.round((uploadedBytes / file.size) * 100);
      setProgress(progressPercent);
    }
    
    return completedParts;
  };

  const uploadPart = (url, partData, partNumber, uploadedBytes) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      // Track upload progress for this part
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const partProgress = uploadedBytes + event.loaded;
          const totalProgress = Math.round((partProgress / file.size) * 100);
          setProgress(totalProgress);
        }
      });
      
      // Handle completion
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const etag = xhr.getResponseHeader('ETag');
          if (!etag) {
            reject(new Error(`No ETag received for part ${partNumber}`));
          } else {
            resolve(etag);
          }
        } else {
          reject(new Error(`Part ${partNumber} upload failed with status ${xhr.status}`));
        }
      });
      
      // Handle errors
      xhr.addEventListener('error', () => {
        reject(new Error(`Part ${partNumber} upload failed due to network error`));
      });
      
      xhr.addEventListener('abort', () => {
        reject(new Error(`Part ${partNumber} upload was cancelled`));
      });
      
      // Upload the part
      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(partData);
    });
  };


  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold mb-4">Upload Asset</h3>
        
        {/* Project Selection */}
        {!projectId && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Project
            </label>
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
              disabled={uploading}
              required
            >
              <option value="">Choose a project...</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.title} ({project.client_name || project.client_id})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* File Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select File
          </label>
          <input
            type="file"
            onChange={handleFileSelect}
            accept="video/*,image/*,.zip,.pdf,.doc,.docx"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
            disabled={uploading}
          />
          {file && (
            <p className="text-sm text-gray-600 mt-1">
              Selected: {file.name} ({formatFileSize(file.size)})
            </p>
          )}
        </div>

        {/* Description Field */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description <span className="text-gray-500">(optional)</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe this asset for the client (e.g., 'Aerial photos of the north wing', 'Final edited video with music')"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500 resize-none"
            rows="3"
            maxLength="1000"
            disabled={uploading}
          />
          <p className="text-xs text-gray-500 mt-1">
            {description.length}/1000 characters
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            {error}
          </div>
        )}

        {/* Progress Bar */}
        {uploading && (
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Uploading...</span>
              <span>{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-brand-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Please keep this window open until upload completes
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={uploadFile}
            disabled={!file || !selectedProjectId || uploading}
            className={`flex-1 px-4 py-2 rounded-md font-medium ${
              !file || !selectedProjectId || uploading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-brand-blue-600 text-white hover:bg-brand-blue-700'
            }`}
          >
            {uploading ? `Uploading... ${progress}%` : 'Upload File'}
          </button>
          
          {uploading ? (
            <button
              onClick={cancelUpload}
              className="px-4 py-2 rounded-md font-medium bg-red-600 text-white hover:bg-red-700"
            >
              Cancel Upload
            </button>
          ) : (
            <button
              onClick={onCancel}
              className="px-4 py-2 rounded-md font-medium bg-gray-600 text-white hover:bg-gray-700"
            >
              Close
            </button>
          )}
        </div>

        {/* Upload Tips */}
        <div className="mt-4 text-xs text-gray-500">
          <p><strong>Tips:</strong></p>
          <ul className="list-disc list-inside space-y-1">
            <li>Maximum file size: 5GB</li>
            <li>Supported formats: Videos, Images, ZIP, PDF, Documents</li>
            <li>Upload through secure worker endpoint</li>
            <li>Progress bar shows real upload progress</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AssetUploadForm;
